import { combineReducers, configureStore, Reducer } from '@reduxjs/toolkit';
import {
    Application,
    Comment,
    ExtraBot,
    Hook,
    Model,
    Network as NetworkStore,
    Platform,
    Share,
    studio,
    TinyMCE,
    Toast,
    TVDisplay,
    UserCurrent,
    WedocApp,
    Bookmark,
    MediaLibrary,
    MagicPad,
    jland,
    Faq,
    AssetReservation,
    Animations,
    AdminLogs,
    Widget,
    WidgetEditor,
    ContentReport,
} from 'jamespot-front-business';
import jApi, {
    ApplicationType,
    HookListType,
    jUserList,
    Model as ModelType,
    NetworkType,
    TinyMCECommonOptions,
    TinyMCEExtendedOptions,
} from 'jamespot-user-api';
import { useDispatch, useSelector } from 'react-redux';
import { logger } from './middleware';
import {
    articleActions,
    ArticleActionsType,
    ArticleSelectorType,
    articleSlice,
    articlesSelector,
} from './slice/Article.slice';
import { genericActions, GenericActionsType } from './slice/Generic.actions';
import { userActions, UserActionsType, userSlice, usersSelector, UsersSelectorType } from './slice/User.slice';
import { reducer as formReducer } from 'redux-form';

export interface JInjectStore extends ReturnType<typeof createStore> {
    asyncReducers: AsyncReducers;
    add: <T extends keyof Omit<AsyncReducers, 'extensions'>>(key: T, asyncReducer: AsyncReducers[T]) => void;
    addExtensionStore: (key: string, asyncReducer: Reducer) => void;
    selectors: {
        article: ArticleSelectorType;
        application: typeof Application.selectors;
        model: typeof Model.selectors;
        user: UsersSelectorType;
        userCurrent: typeof UserCurrent.selectors;
        hooks: typeof Hook.selectors;
    };
    actions: {
        article: ArticleActionsType;
        application: typeof Application.actions;
        model: typeof Model.actions;
        user: UserActionsType;
        userCurrent: typeof UserCurrent.actions;
        generic: GenericActionsType;
        hooks: typeof Hook.actions;
    };
}

export type AsyncReducers = {
    [AdminLogs.slice.name]: typeof AdminLogs.slice.reducer;
    [Animations.slice.name]: typeof Animations.slice.reducer;
    [AssetReservation.slice.name]: typeof AssetReservation.slice.reducer;
    [Bookmark.slice.name]: typeof Bookmark.slice.reducer;
    [Comment.slice.name]: typeof Comment.slice.reducer;
    [ExtraBot.slice.name]: typeof ExtraBot.slice.reducer;
    [ContentReport.slice.name]: typeof ContentReport.slice.reducer;
    [Faq.slice.name]: typeof Faq.slice.reducer;
    [MagicPad.slice.name]: typeof MagicPad.slice.reducer;
    [MediaLibrary.slice.name]: typeof MediaLibrary.slice.reducer;
    [Share.slice.name]: typeof Share.slice.reducer;
    [TVDisplay.slice.name]: typeof TVDisplay.slice.reducer;
    [WedocApp.slice.name]: typeof WedocApp.slice.reducer;
    [Widget.slice.name]: typeof Widget.slice.reducer;
    [jland.slice.name]: typeof jland.slice.reducer;
    [studio.slice.name]: typeof studio.slice.reducer;
    [WidgetEditor.slice.name]: typeof WidgetEditor.slice.reducer;
} & { extensions: { [key: string]: Reducer } };

// Define the Reducers that will always be present in the application
const staticReducers = {
    comment: Comment.slice.reducer,
    entities: combineReducers({
        applications: Application.slice.reducer,
        models: Model.slice.reducer,
        users: userSlice.reducer,
        articles: articleSlice.reducer,
    }),
    form: formReducer as Reducer, // FIXME : Redux-form portability
    hooks: Hook.slice.reducer,
    network: NetworkStore.slice.reducer,
    platform: Platform.slice.reducer,
    toasts: Toast.slice.reducer,
    tinymce: TinyMCE.slice.reducer,
    userCurrent: UserCurrent.slice.reducer,
};

// Configure the store
export function makeStore(jValues: {
    tinymceCommonOptions: TinyMCECommonOptions;
    tinymceExtendedOptions: TinyMCEExtendedOptions;
    models: ModelType[];
    applications: ApplicationType[];
    networks: {
        networkPublic: NetworkType;
        networkMe: NetworkType;
    };
    platform: {
        userAccountStatus: 0 | 1;
        userHighlightFields: string[];
    };
    hooks: HookListType;
    userCurrent: jUserList | undefined;
}): JInjectStore {
    // Create the store with the initial state, enforcing type for dispatch usage.
    const initialAsyncReducers: AsyncReducers = {
        extensions: { EMPTY: (state = {}) => state },
    } as unknown as AsyncReducers;

    const store = createStore(initialAsyncReducers);

    const jStore = {
        ...store,
        asyncReducers: initialAsyncReducers,
        add: <T extends keyof Omit<AsyncReducers, 'extensions'>>(
            key: T,
            asyncReducer: AsyncReducers[T],
            relatedAppId?: string,
        ): void => {
            let isRelatedAppActivate = false;
            if (relatedAppId) {
                const curState = jStore.getState();
                isRelatedAppActivate = typeof curState?.entities?.applications?.entities[relatedAppId] !== 'undefined';
            }
            const mustBeAddToReducer = !relatedAppId ? true : isRelatedAppActivate;

            if (!key || jStore.asyncReducers[key] || !mustBeAddToReducer) {
                return;
            }
            jStore.asyncReducers[key] = asyncReducer;
            jStore.replaceReducer(createReducer(jStore.asyncReducers));
        },
        addExtensionStore: (key: string, asyncReducer: Reducer, relatedAppId?: string): void => {
            let isRelatedAppActivate = false;
            if (relatedAppId) {
                const curState = jStore.getState();
                isRelatedAppActivate = typeof curState?.entities?.applications?.entities[relatedAppId] !== 'undefined';
            }
            const mustBeAddToReducer = !relatedAppId ? true : isRelatedAppActivate;

            if (!key || jStore.asyncReducers.extensions[key] || !mustBeAddToReducer) {
                return;
            }
            jStore.asyncReducers.extensions[key] = asyncReducer;
            jStore.replaceReducer(createReducer(jStore.asyncReducers));
        },
        selectors: {
            article: articlesSelector,
            application: Application.selectors,
            model: Model.selectors,
            user: usersSelector,
            userCurrent: UserCurrent.selectors,
            network: NetworkStore.selectors,
            hooks: Hook.selectors,
        },
        actions: {
            article: articleActions,
            application: Application.actions,
            model: Model.actions,
            user: userActions,
            userCurrent: UserCurrent.actions,
            generic: genericActions,
            network: NetworkStore.actions,
            hooks: Hook.actions,
        },
    };

    if (jValues.userCurrent) {
        store.dispatch(UserCurrent.actions.init(jValues.userCurrent));
    }

    store.dispatch(Application.actions.addMany(jValues.applications));
    store.dispatch(Model.actions.addMany(jValues.models));
    store.dispatch(NetworkStore.actions.initNetworkStatics(jValues.networks));
    store.dispatch(
        TinyMCE.actions.initOptions({ common: jValues.tinymceCommonOptions, extended: jValues.tinymceExtendedOptions }),
    );
    store.dispatch(Platform.actions.initPlatformConfig(jValues.platform));
    store.dispatch(Hook.actions.initHooks(jValues.hooks));

    return jStore;
}

function createReducer(asyncReducers: AsyncReducers) {
    const { extensions, ...asyncReducersWithoutExtensions } = asyncReducers;
    return combineReducers({
        extensions: combineReducers(extensions),
        ...asyncReducersWithoutExtensions,
        ...staticReducers,
    });
}

function createStore(initialAsyncReducers: AsyncReducers) {
    return configureStore({
        reducer: createReducer(initialAsyncReducers),
        middleware: (getDefaultMiddleware) =>
            getDefaultMiddleware({ thunk: { extraArgument: { jApi } } }).concat(logger),
    });
}

export type AppDispatch = ReturnType<typeof createStore>['dispatch'];
export type RootState<T extends keyof AsyncReducers | keyof typeof staticReducers> = Required<
    Pick<ReturnType<ReturnType<typeof createStore>['getState']>, T>
>;

export const useAppDispatch = () => useDispatch<AppDispatch>();

export function useAppSelector<S extends keyof AsyncReducers | keyof typeof staticReducers, R = unknown>(
    selector: (state: RootState<S>) => R,
    equalityFn?: (left: R, right: R) => boolean,
) {
    return useSelector<RootState<S>, R>(selector, equalityFn);
}
